<template>
  <v-container class="capture-seats">
    <div class="d-flex justify-space-between align-center flex-wrap mb-4">
      <h1 class="form-title">Active Seats</h1>
      <div class="d-flex align-center ga-3">
        <v-select
          v-model="period"
          :items="periods"
          label="Period"
          variant="outlined"
          density="compact"
          hide-details
          style="min-width: 13rem"
          data-testid="period-select"
        ></v-select>
        <v-btn
          variant="text"
          :disabled="!usage.devices.length"
          @click="exportCsv"
          data-testid="export-btn"
        >
          <v-icon size="small" class="mr-1">fas fa-download</v-icon>
          Export CSV
        </v-btn>
      </div>
    </div>

    <v-alert v-if="unprovisioned" type="info" variant="tonal" data-testid="unprovisioned-alert">
      No Structure Capture account is linked to this login yet. Contact
      <a href="mailto:partners@structure.io">partners@structure.io</a> to have one set up.
    </v-alert>

    <template v-else>
      <div class="d-flex align-center ga-6 flex-wrap">
        <ScoreCard title="Active Seats" :metric="usage.active_device_count" :subtitle="periodLabel" />
        <!-- The billing rule, stated where someone questioning an invoice will look. -->
        <p class="seat-rule">
          A device that ran Structure Capture on any single day of the period counts as one
          seat. There is no proration and no partial seat.
        </p>
      </div>

      <v-data-table
        :headers="headers"
        :items="usage.devices"
        :loading="loading"
        :search="search"
        data-testid="seats-table"
      >
        <template #top>
          <v-text-field
            v-model="search"
            label="Search by name, serial or model"
            prepend-inner-icon="fas fa-search"
            single-line
            hide-details
            variant="outlined"
            density="compact"
            class="ma-2"
          ></v-text-field>
        </template>
        <template #[`item.device_name`]="{ item }">{{ item.device_name || '—' }}</template>
        <template #[`item.device_serial`]="{ item }">
          <span class="mono">{{ item.device_serial || '—' }}</span>
        </template>
        <template #[`item.first_seen`]="{ item }">{{ formatDate(item.first_seen) }}</template>
        <template #[`item.last_seen`]="{ item }">{{ formatDate(item.last_seen) }}</template>
        <template #no-data>No devices activated in this period.</template>
      </v-data-table>
    </template>
  </v-container>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { reasonFor, useCBERequest } from '@/composables';
import { downloadCsv, SEAT_CSV_COLUMNS } from '@/utils/csv';
import { currentMonth, monthOptions } from '@/utils/months';
import ScoreCard from '@/components/charts/ScoreCard.vue';

const store = useStore();
const { makeCBERequest } = useCBERequest();

const UTC = { timeZone: 'UTC' };
const MS_PER_DAY = 86400000;

const usage = ref({ period: null, active_device_count: 0, devices: [] });
const loading = ref(false);
const unprovisioned = ref(false);
const search = ref('');

const periods = monthOptions();
const period = ref(currentMonth());

const headers = [
  { title: 'Device Name', key: 'device_name' },
  { title: 'Serial Number', key: 'device_serial' },
  { title: 'Model', key: 'device_model' },
  { title: 'App Version', key: 'app_version' },
  { title: 'First Seen', key: 'first_seen' },
  { title: 'Last Active', key: 'last_seen' },
];

// UTC dates, matching the window that counted them: shown locally, a device that checked
// in just after UTC midnight would carry a date outside the period it is billed in.
const formatDate = (value) =>
  (value ? new Date(value).toLocaleDateString(undefined, UTC) : '—');

// `period.end` is exclusive, so showing it raw would claim a day the period does not
// cover — the customer would read a month as ending on the 1st of the next one.
//
// Formatted in UTC because these are calendar bounds, not instants: the window runs
// UTC-midnight to UTC-midnight, so anyone west of Greenwich would otherwise see a
// September period labelled 8/31 – 9/29. Said on the label, because the device
// timestamps below are deliberately local and could sit a day outside it.
const periodLabel = computed(() => {
  const window = usage.value.period;
  if (!window) return '';
  const start = new Date(window.start);
  const lastDay = new Date(new Date(window.end).getTime() - MS_PER_DAY);
  const bounds = [start, lastDay].map(day => day.toLocaleDateString(undefined, UTC));
  return `${bounds[0]} – ${bounds[1]} UTC`;
});

const fetchUsage = async () => {
  loading.value = true;
  try {
    const response = await makeCBERequest(
      '/piccolo/capture/usage',
      'get',
      {},
      { period: period.value },
      { rethrow: true }
    );
    if (response) usage.value = response;
    unprovisioned.value = false;
  } catch (error) {
    if (error.response?.status === 404) {
      unprovisioned.value = true;
    } else {
      store.commit('SET_ERROR_MESSAGE', reasonFor(error));
      store.commit('SET_SHOW_ERROR_MODAL', true);
    }
  } finally {
    loading.value = false;
  }
};

const exportCsv = () => {
  downloadCsv(`capture-seats-${period.value}.csv`, SEAT_CSV_COLUMNS, usage.value.devices);
};

// An empty value from clearing the input would ask the API for the current month, which
// silently contradicts the box the customer is looking at.
watch(period, (value) => {
  if (value) fetchUsage();
});

onMounted(fetchUsage);
</script>

<style scoped lang="scss">
@import '@/assets/styles/StrVariables.scss'

.mono
  font-family: monospace

.seat-rule
  max-width: 32rem
</style>
